import { createSelector, createSelectorMemoized } from '@base-ui/utils/store';
import { schedulerPreferenceSelectors } from '@mui/x-scheduler-internals/scheduler-selectors';
import {
  getDisplayedHourRange,
  getTimelineAxisDurationMs,
  FULL_DAY_MINUTES,
} from '@mui/x-scheduler-internals/internals';
import type { EventTimelinePremiumPresetConfig } from '../models/preset';
import type { EventTimelinePremiumState as State } from '../use-event-timeline-premium';
import { EVENT_TIMELINE_PREMIUM_PRESET_DEFINITIONS } from '../internals/utils/preset-utils';

export const eventTimelinePremiumPresetSelectors = {
  preset: createSelector((state: State) => state.preset),
  presets: createSelector((state: State) => state.presets),
  config: createSelectorMemoized(
    (state: State) => state.adapter,
    (state: State) => state.visibleDate,
    (state: State) => state.preset,
    // Primitive inputs so an inline `presetConfig` literal cannot defeat the
    // memoization (the object identity changes on every parent render).
    (state: State) =>
      state.presetConfig[state.preset as keyof EventTimelinePremiumPresetConfig]?.startTime,
    (state: State) =>
      state.presetConfig[state.preset as keyof EventTimelinePremiumPresetConfig]?.endTime,
    schedulerPreferenceSelectors.weekStartsOn,
    (adapter, visibleDate, preset, presetStartTime, presetEndTime, weekStartsOn) => {
      const config = EVENT_TIMELINE_PREMIUM_PRESET_DEFINITIONS[preset];
      if (!config) {
        throw new Error(
          `MUI X Scheduler: No configuration registered for preset "${preset}". ` +
            `TimelineGridHeader cannot derive header rows or grid sizing without a config, so the timeline would render incorrectly. ` +
            `Use one of the built-in presets ("dayAndHour", "dayAndMonth", "dayAndWeek", "monthAndYear", "year").`,
        );
      }
      const {
        getStartDate,
        getEndDate,
        unitCount,
        getCssUnitCount,
        tickWidth,
        headers,
        timeResolution,
      } = config;
      const start = getStartDate(adapter, visibleDate, weekStartsOn);
      const end = getEndDate(adapter, start, unitCount, weekStartsOn);

      // Only hour-resolution presets can trim their visible hours. The range itself stays
      // midnight-based: the hour window is applied through `dayStartMinute` / `dayEndMinute`.
      const hourRange =
        timeResolution === 'hour'
          ? getDisplayedHourRange(presetStartTime, presetEndTime, `presetConfig.${preset}`)
          : { startTime: 0, endTime: 24 };
      const dayStartMinute = hourRange.startTime * 60;
      const dayEndMinute = hourRange.endTime * 60;

      // Preset callbacks size the grid for the full day; the hour window scales the
      // tick count once here instead of threading it through every preset.
      const fullDayTickCount = getCssUnitCount ? getCssUnitCount(adapter, start, end) : unitCount;
      const tickCount =
        timeResolution === 'hour'
          ? (fullDayTickCount * (dayEndMinute - dayStartMinute)) / FULL_DAY_MINUTES
          : fullDayTickCount;

      return {
        tickCount,
        start,
        end,
        tickWidth,
        headers,
        timeResolution,
        dayStartMinute,
        dayEndMinute,
        // Precomputed once per config change: per-row hooks read it on every render.
        durationMs: getTimelineAxisDurationMs(adapter, {
          start,
          end,
          dayStartMinute,
          dayEndMinute,
        }),
      };
    },
  ),
};
